import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bot, User, Trash2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChatWelcome } from "@/components/ChatWelcome";

import { chatMessageSchema } from "@/lib/validation";
import { useClearChat } from "@/hooks/useClearChat";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Clean markdown formatting from AI responses
// Only removes actual markdown syntax, preserves underscores in words like "tax_savings"
const cleanMarkdown = (text: string): string => {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Bold **text** → text
    .replace(/\*([^*]+)\*/g, '$1')     // Italic *text* → text
    .replace(/__([^_]+)__/g, '$1')     // Bold __text__ → text
    .replace(/(?<!\w)_([^_]+)_(?!\w)/g, '$1') // Italic _text_ → text (not word_with_underscores)
    .replace(/#{1,6}\s/g, '')          // Headers
    .replace(/`{3}[\s\S]*?`{3}/g, '')  // Code blocks
    .replace(/`([^`]+)`/g, '$1')       // Inline code `text` → text
    .replace(/^\s*[-*+]\s/gm, '')      // Bullet points
    .replace(/^\s*\d+\.\s/gm, '')      // Numbered lists
    .trim();
};

// Rate limit: 5 messages per minute
const RATE_LIMIT_COUNT = 5;
const RATE_LIMIT_WINDOW_MS = 60000;

export default function Chat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [receiptCount, setReceiptCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messageTimestampsRef = useRef<number[]>([]);
  const lastSendTimeRef = useRef<number>(0);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Check rate limit
  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();
    // Filter timestamps within the window
    messageTimestampsRef.current = messageTimestampsRef.current.filter(
      (ts) => now - ts < RATE_LIMIT_WINDOW_MS
    );
    
    if (messageTimestampsRef.current.length >= RATE_LIMIT_COUNT) {
      const oldestTimestamp = messageTimestampsRef.current[0];
      const waitTime = Math.ceil((RATE_LIMIT_WINDOW_MS - (now - oldestTimestamp)) / 1000);
      toast({
        title: "Slow down!",
        description: `You can send ${RATE_LIMIT_COUNT} messages per minute. Try again in ${waitTime}s.`,
        variant: "destructive",
      });
      return false;
    }
    return true;
  }, [toast]);

  // Debounce check to prevent double-tap on mobile
  const canSend = useCallback((): boolean => {
    const now = Date.now();
    const DEBOUNCE_MS = 500;
    if (now - lastSendTimeRef.current < DEBOUNCE_MS) {
      return false;
    }
    lastSendTimeRef.current = now;
    return true;
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadConversationHistory();
    loadReceiptCount();
  }, []);

  // Reset local state when chat is cleared (from mobile menu or desktop)
  const resetChatState = useCallback(() => {
    setMessages([]);
    setShowWelcome(true);
    setIsReturningUser(false);
  }, []);

  // Shared clear chat hook - used by both desktop and mobile
  const { clearChat, isClearing } = useClearChat();

  // Listen for clear chat event (dispatched by shared hook)
  useEffect(() => {
    window.addEventListener("clearChatHistory", resetChatState);
    return () => window.removeEventListener("clearChatHistory", resetChatState);
  }, [resetChatState]);

  const loadReceiptCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { count } = await supabase
        .from("receipts")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      setReceiptCount(count || 0);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error loading receipt count:", error);
      }
    }
  };

  const loadConversationHistory = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("conversation_history")
        .select("role, content, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setMessages(data.map(msg => ({
          role: msg.role as "user" | "assistant",
          content: msg.content
        })));
        setShowWelcome(false);
        setIsReturningUser(true);
      } else {
        setShowWelcome(true);
        setIsReturningUser(false);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error loading conversation history:", error);
      }
    } finally {
      setIsLoadingHistory(false);
    }
  };


  const streamChat = async (userMessage: string, retryCount = 0): Promise<boolean> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Authentication Required",
        description: "Please log in to use the chat feature.",
        variant: "destructive",
      });
      return false;
    }

    const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
    
    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          messages: [{ role: "user", content: userMessage }]
        }),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMessage = errorData.error || "Something went wrong";
        
        // Handle quota/billing errors - show friendly message, don't retry
        if (resp.status === 429 || resp.status === 402) {
          toast({
            title: "AI Paused",
            description: errorMessage,
            variant: "destructive",
          });
          return false;
        }
        
        // Retry once on other errors
        if (retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return streamChat(userMessage, 1);
        }
        
        toast({
          title: "Connection Issue",
          description: "Couldn't reach the server. Check your connection and try again.",
          variant: "destructive",
        });
        return false;
      }

      if (!resp.body) {
        if (retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return streamChat(userMessage, 1);
        }
        throw new Error("No response body");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;
      let assistantSoFar = "";

      // Add empty assistant message
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantSoFar += content;
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage?.role === "assistant") {
                  lastMessage.content = assistantSoFar;
                }
                return newMessages;
              });
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Stream error:", error);
      }
      
      // Retry once on network errors
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return streamChat(userMessage, 1);
      }
      
      toast({
        title: "Connection Issue",
        description: "Couldn't reach the server. Check your connection and try again.",
        variant: "destructive",
      });
      // Remove the empty assistant message on error
      setMessages(prev => prev.slice(0, -1));
      return false;
    }
  };

  const handleSend = useCallback(async (messageToSend?: string) => {
    const message = (messageToSend || input).trim();
    
    // Prevent empty messages
    if (!message || message.length === 0) {
      return;
    }
    
    // Prevent sending while loading
    if (isLoading) {
      return;
    }
    
    // Debounce check for mobile double-tap
    if (!canSend()) {
      return;
    }
    
    // Check rate limit
    if (!checkRateLimit()) {
      return;
    }

    // Validate message before sending
    try {
      chatMessageSchema.parse({ content: message });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Invalid message",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
      return;
    }

    // Record timestamp for rate limiting
    messageTimestampsRef.current.push(Date.now());

    setInput("");
    setIsLoading(true);
    setShowWelcome(false);

    setMessages((prev) => [...prev, { role: "user", content: message }]);
    
    const success = await streamChat(message);
    
    // If failed after retry, remove the user message to allow resending
    if (!success) {
      setMessages(prev => prev.filter((_, i) => i !== prev.length - 1 || prev[prev.length - 1]?.role !== "user"));
    }
    
    setIsLoading(false);
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [input, isLoading, canSend, checkRateLimit, toast]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    
    // Auto-resize textarea
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Header - desktop only */}
      {!isMobile && (
        <div className="border-b border-border p-4 bg-card hidden md:flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-semibold text-lg">Tax Assistant</h2>
            <p className="text-sm text-muted-foreground">Your personal tax expert</p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" disabled={isClearing}>
                <Trash2 className="w-4 h-4" />
                {isClearing ? "Clearing..." : "Clear Chat"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear Chat History?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete your entire conversation history. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={clearChat}>
                  Clear History
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Messages area */}
      <div 
        className="flex-1 overflow-y-auto w-full"
        style={{ paddingBottom: isMobile ? '140px' : '120px' }}
      >
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-base text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <>
            {showWelcome && messages.length === 0 ? (
              <div className="h-full flex items-center justify-center p-4 md:p-6">
                <ChatWelcome onSuggestionClick={(msg) => handleSend(msg)} />
              </div>
            ) : (
              <div className="max-w-2xl mx-auto px-4 py-4 space-y-5">
                
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    {message.role === "assistant" && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-3 max-w-[85%] ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground"
                      }`}
                    >
                      <p className="text-[16px] md:text-[15px] whitespace-pre-wrap leading-relaxed break-words">
                        {message.role === "assistant" ? cleanMarkdown(message.content) : message.content}
                      </p>
                    </div>
                    {message.role === "user" && (
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="w-4 h-4 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Fixed Input area - ChatGPT style */}
      <div 
        className="fixed bottom-0 left-0 right-0 md:left-64 bg-gradient-to-t from-background via-background to-transparent z-30"
        style={{
          paddingBottom: isMobile ? 'calc(16px + env(safe-area-inset-bottom))' : '16px',
          paddingTop: '24px'
        }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-card rounded-2xl shadow-lg border border-border/50 p-2 flex gap-2 items-end">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask about taxes, deductions, receipts..."
              className="flex-1 min-h-[48px] max-h-[150px] resize-none text-[16px] bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-3 py-3 leading-normal placeholder:text-muted-foreground/50"
              disabled={isLoading}
              rows={1}
            />
            <Button 
              onClick={() => handleSend()} 
              className="h-11 w-11 rounded-xl flex-shrink-0"
              disabled={isLoading || !input.trim()}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span className="text-lg">↑</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
